import z from 'zod';
/**
 * Project-level utilisation summary (DR-524) — the aircraft's last flight date
 * and the total hours/cycles at that flight.
 *
 * Emitted as ONE webhook per project. Across the project's Utilisation-Records
 * documents the one with the most recent flight date wins (a single winning
 * document, mirroring the AD/SB/QEC posture), so the payload is a flat scalar
 * record rather than an array. `sourcePageId` deep-links to the exact page the
 * winning flight date was read from; `documentId` / `reconstructedDocumentId`
 * identify the winning document. Every value field is nullable — a project may
 * carry a Utilisation-Records document that omits some scalars.
 *
 * DR-3767: the winning source must belong to the aircraft the engine's install/removal
 * history says it is on, and a `Current TSN and CSN Statement` competes on the same
 * as-of date axis (`sourceCategory` names the lane). An envelope whose value fields are
 * ALL null and which carries `unavailableReason` is a deliberate WITHDRAWAL — the
 * consumer must clear the row it previously painted, not ignore the delivery.
 */
export const utilisationSummaryReadyWebhookSchema = z.looseObject({
    type: z.literal('utilisation-summary-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    lastFlightDate: z.string().nullable(),
    reportDate: z.string().nullable(),
    latestTAH: z.number().nullable(),
    latestTAC: z.number().nullable(),
    flightFrom: z.string().nullable(),
    flightTo: z.string().nullable(),
    aircraftRegistration: z.string().nullable(),
    sourcePageId: z.string().nullable(),
    documentId: z.string().nullable(),
    reconstructedDocumentId: z.string().nullable().optional(),
    // DR-3767. `.optional()` for the same rollout reason as `generatedAt`: this schema is a
    // member of `webhookPayloadSchema`, so a required field would make an upgraded consumer
    // reject every envelope Brain emitted before the deploy that added them.
    sourceCategory: z.enum(['Utilisation Records', 'Current TSN and CSN Statement']).nullable().optional(),
    aircraftMsn: z.string().nullable().optional(),
    unavailableReason: z
        .enum(['no-usable-source', 'no-matching-aircraft', 'aircraft-unknown', 'engine-detached'])
        .nullable()
        .optional(),
});
//# sourceMappingURL=utilisation-summary-ready.webhook.js.map